import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Switch } from 'src/@/components/ui/switch';
import Iconify from 'src/components/Iconify';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import widgetCmsService from 'src/services/widgetCmsService';
import RichEditor from './RichEditor';
import { WIDGET_CMS_DESIGN as D } from './WidgetCms.design';

export default function HelpArtigoDrawer({ categoria, artigo, onClose, onSaved }) {
    const { t } = useTranslation('settings');
    const isEdit = !!artigo?._id;
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    const [ordem, setOrdem] = useState(0);
    const [publicado, setPublicado] = useState(true);
    const [corpo, setCorpo] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (artigo) {
            setTitulo(artigo.titulo || '');
            setDescricao(artigo.descricao || '');
            setOrdem(artigo.ordem || 0);
            setPublicado(artigo.publicado !== false);
            setCorpo(artigo.corpo || '');
        }
    }, [artigo]);

    const handleSave = async () => {
        if (!titulo.trim()) {
            setError(t('widgetCms.helpArticle.titleRequiredError', 'O título é obrigatório'));
            return;
        }
        setSaving(true);
        setError(null);
        const data = { titulo, descricao, ordem: Number(ordem) || 0, publicado, corpo };
        try {
            if (isEdit) {
                await widgetCmsService.editarArtigo(artigo._id, data);
            } else {
                await widgetCmsService.criarArtigo(categoria._id, data);
            }
            onSaved();
        } catch (e) {
            setError(e?.message || t('widgetCms.helpArticle.saveError', 'Erro ao salvar o artigo'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={D.drawer.overlay}>
            <div className={D.drawer.backdrop} onClick={onClose} />
            <div className={D.drawer.containerNarrow}>
                <div className={D.drawer.header.container}>
                    <div className={D.drawer.header.left}>
                        <div className={`${D.drawer.header.iconBox} ${isEdit ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-[#7C3AED]'}`}>
                            <Iconify icon="solar:book-bookmark-bold" width={20} />
                        </div>
                        <div>
                            <h2 className={D.drawer.header.title}>{isEdit ? t('widgetCms.helpArticle.editTitle', 'Editar artigo') : t('widgetCms.helpArticle.createTitle', 'Novo artigo')}</h2>
                            <p className={D.drawer.header.subtitle}>
                                {t('widgetCms.helpArticle.categoryLabel', 'Categoria:')} {categoria?.emoji} {categoria?.titulo}
                            </p>
                        </div>
                    </div>
                    <button className={D.drawer.header.closeButton} onClick={onClose}>
                        <Iconify icon="eva:close-outline" width={20} />
                    </button>
                </div>

                {error && (
                    <div className={D.drawer.errorBanner}>
                        <Iconify icon="eva:alert-circle-outline" width={16} />
                        {error}
                    </div>
                )}

                <div className={D.drawer.content.full}>
                    <div className="max-w-3xl mx-auto flex flex-col gap-5">
                        <div>
                            <label className={D.drawer.section.label}>{t('labels.title', 'Título', { ns: 'common' })}</label>
                            <Input
                                value={titulo}
                                onChange={e => setTitulo(e.target.value)}
                                placeholder={t('widgetCms.helpArticle.titlePlaceholder', 'Ex.: Como instalar o widget no seu site')}
                                className={D.drawer.section.input}
                            />
                        </div>
                        <div>
                            <label className={D.drawer.section.label}>{t('widgetCms.shared.shortDescriptionLabel', 'Descrição curta')}</label>
                            <Textarea
                                value={descricao}
                                onChange={e => setDescricao(e.target.value)}
                                placeholder={t('widgetCms.helpArticle.descriptionPlaceholder', 'Resumo exibido na lista de artigos e nos resultados de busca')}
                                rows={2}
                                className={D.drawer.section.input}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={D.drawer.section.label}>{t('widgetCms.helpArticle.orderLabel', 'Ordem')}</label>
                                <Input
                                    type="number"
                                    value={ordem}
                                    onChange={e => setOrdem(e.target.value)}
                                    className={D.drawer.section.input}
                                />
                            </div>
                            <div className="flex items-end pb-1">
                                <div className="flex items-center gap-3">
                                    <Switch checked={publicado} onCheckedChange={setPublicado} />
                                    <span className="text-sm text-gray-700">{t('status.published', 'Publicado', { ns: 'common' })}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className={D.drawer.section.label}>{t('widgetCms.shared.contentLabel', 'Conteúdo')}</label>
                            <RichEditor value={corpo} onChange={setCorpo} placeholder={t('widgetCms.helpArticle.contentPlaceholder', 'Escreva o artigo...')} />
                        </div>
                    </div>
                </div>

                <div className={D.drawer.footer.container}>
                    <div className={D.drawer.footer.left} />
                    <div className={D.drawer.footer.right}>
                        <button
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                            onClick={onClose}
                        >
                            {t('buttons.cancel', 'Cancelar', { ns: 'common' })}
                        </button>
                        <ButtonLoading loading={saving} onClick={handleSave} className={D.page.actionButton}>
                            <Iconify icon="eva:save-outline" width={16} />
                            {isEdit ? t('buttons.saveChanges', 'Salvar alterações', { ns: 'common' }) : t('widgetCms.helpArticle.createButton', 'Criar artigo')}
                        </ButtonLoading>
                    </div>
                </div>
            </div>
        </div>
    );
}

HelpArtigoDrawer.propTypes = {
    categoria: PropTypes.object.isRequired,
    artigo: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
};
